import { LogOut, User, Settings, FileText, Shield, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';

interface HeaderProps {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}

export default function Header({ title, subtitle, children }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const handleLogout = () => {
    // Limpar localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // Chamar logout do contexto (isso vai atualizar o estado e desconectar o WebSocket)
    logout();

    // Redirecionar para login
    navigate('/login');
  };

  return (
    <header className="bg-bg-2 border-b border-bg-3 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          {/* Logo GeniAI */}
          <div className="flex items-center gap-4 mr-6">
            <img
              src="/geniAI1.webp"
              alt="GeniAI Logo"
              className="h-10 w-auto"
            />
          </div>

          {/* Título e Subtítulo */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-text-1">{title}</h1>
            {subtitle && (
              <p className="text-text-3 text-sm mt-1">{subtitle}</p>
            )}
          </div>

          {/* Lado direito: children + user menu */}
          <div className="flex items-center gap-3">
            {children}

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Notification Bell */}
            {user && <NotificationBell />}

            {/* User Menu Dropdown */}
            {user && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-bg-3 transition-colors"
                >
                  <div className="w-8 h-8 bg-accent-1 rounded-full flex items-center justify-center">
                    <User size={16} className="text-white" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm text-text-1 font-medium">{user.name}</p>
                    <p className="text-xs text-text-3">{user.email}</p>
                  </div>
                  <svg
                    className={`w-4 h-4 text-text-3 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-bg-1 border border-border-1 rounded-lg shadow-xl py-1 z-50">
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-bg-3">
                      <p className="text-sm font-medium text-text-1">{user.name}</p>
                      <p className="text-xs text-text-3">{user.email}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-accent-1/20 text-accent-1 capitalize">
                        {user.role?.replace('_', ' ')}
                      </span>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <Link
                        to="/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-text-2 hover:bg-bg-3 hover:text-text-1 transition-colors"
                      >
                        <User size={16} />
                        Meu Perfil
                      </Link>

                      {isAdmin && (
                        <>
                          <div className="border-t border-bg-3 my-1" />
                          <div className="px-4 py-1">
                            <p className="text-xs text-text-3 font-medium uppercase tracking-wider">
                              Administração
                            </p>
                          </div>
                          <Link
                            to="/admin/dashboard"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-text-2 hover:bg-bg-3 hover:text-text-1 transition-colors"
                          >
                            <LayoutDashboard size={16} />
                            Painel Admin
                          </Link>
                          <Link
                            to="/admin/waba-access"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-text-2 hover:bg-bg-3 hover:text-text-1 transition-colors"
                          >
                            <Shield size={16} />
                            Acesso WABA
                          </Link>
                          <Link
                            to="/admin/audit-logs"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-text-2 hover:bg-bg-3 hover:text-text-1 transition-colors"
                          >
                            <FileText size={16} />
                            Logs de Auditoria
                          </Link>
                        </>
                      )}

                      {user?.role === 'super_admin' && (
                        <>
                          <Link
                            to="/admin"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-text-2 hover:bg-bg-3 hover:text-text-1 transition-colors"
                          >
                            <Settings size={16} />
                            Painel Super Admin
                          </Link>
                        </>
                      )}
                    </div>

                    {/* Logout */}
                    <div className="border-t border-bg-3 py-1">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleLogout();
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors"
                      >
                        <LogOut size={16} />
                        Sair
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
